import { SnapshotKpis } from "@cryptors/api-client";
import { Paper, Stack, Typography } from "@mui/material";
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

const fmt = (n: number) => new Intl.NumberFormat("fr-FR").format(n);

const PIE_COLORS = [
  "#1976d2",
  "#f57c00",
  "#388e3c",
  "#7b1fa2",
  "#d81b60",
  "#9e9e9e",
];

interface PieEntry {
  name: string;
  value: number;
  pct: number;
}

const Chart = ({ title, data }: { title: string; data: PieEntry[] }) => {
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <Paper sx={{ p: 2, minWidth: 320, flex: 1 }} elevation={1}>
      <Typography variant="caption" color="text.secondary">
        {title}
      </Typography>
      {total === 0 ? (
        <Typography variant="body2" color="text.secondary" mt={2}>
          Aucune donnée
        </Typography>
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={90}
              label={(e: { payload?: PieEntry }) => `${e.payload?.pct ?? 0}%`}
            >
              {data.map((_, i) => (
                <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip formatter={(v) => fmt(Number(v))} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      )}
    </Paper>
  );
};

interface Props {
  data: SnapshotKpis;
}

export const PlayerProfileSection = ({ data }: Props) => {
  const ageEntries: PieEntry[] = [
    {
      name: "< 18 ans",
      value: data.ageBrackets.under18.count,
      pct: data.ageBrackets.under18.pct,
    },
    {
      name: "18–25 ans",
      value: data.ageBrackets["18to25"].count,
      pct: data.ageBrackets["18to25"].pct,
    },
    {
      name: "26–40 ans",
      value: data.ageBrackets["26to40"].count,
      pct: data.ageBrackets["26to40"].pct,
    },
    {
      name: "41–60 ans",
      value: data.ageBrackets["41to60"].count,
      pct: data.ageBrackets["41to60"].pct,
    },
    {
      name: "> 60 ans",
      value: data.ageBrackets.over60.count,
      pct: data.ageBrackets.over60.pct,
    },
  ].filter((e) => e.value > 0);

  const playModeEntries: PieEntry[] = [
    {
      name: "Solo",
      value: data.playMode.solo.count,
      pct: data.playMode.solo.pct,
    },
    {
      name: "Famille",
      value: data.playMode.family.count,
      pct: data.playMode.family.pct,
    },
    {
      name: "Amis",
      value: data.playMode.friends.count,
      pct: data.playMode.friends.pct,
    },
  ].filter((e) => e.value > 0);

  const departmentEntries: PieEntry[] = data.departments.map((d) => ({
    name: d.department,
    value: d.count,
    pct: d.pct,
  }));

  const genderEntries: PieEntry[] = [
    {
      name: "Agent",
      value: data.gender.agent.count,
      pct: data.gender.agent.pct,
    },
    {
      name: "Agente",
      value: data.gender.agente.count,
      pct: data.gender.agente.pct,
    },
    {
      name: "Indéterminé",
      value: data.gender.indetermine.count,
      pct: data.gender.indetermine.pct,
    },
  ].filter((e) => e.value > 0);

  const residenceEntries: PieEntry[] = [
    {
      name: "France",
      value: data.residence.france.count,
      pct: data.residence.france.pct,
    },
    {
      name: "Europe",
      value: data.residence.europe.count,
      pct: data.residence.europe.pct,
    },
    {
      name: "Autre",
      value: data.residence.other.count,
      pct: data.residence.other.pct,
    },
  ].filter((e) => e.value > 0);

  return (
    <Stack gap={2}>
      <Typography variant="h6">
        Profil des joueurs
        {data.under25Ratio !== null && (
          <Typography
            component="span"
            variant="body2"
            color="text.secondary"
            ml={2}
          >
            · Ratio &lt; 25 ans : <strong>{data.under25Ratio}%</strong>
          </Typography>
        )}
      </Typography>
      <Stack direction="row" gap={2} flexWrap="wrap">
        <Chart
          title="Répartition Agent / Agente / Indéterminé"
          data={genderEntries}
        />
        <Chart
          title="Résidence (France / Europe / Autre)"
          data={residenceEntries}
        />
        <Chart title="Tranche d'âge" data={ageEntries} />
        <Chart title="Mode de visite déclaré" data={playModeEntries} />
        <Chart title="Top départements de résidence" data={departmentEntries} />
      </Stack>
    </Stack>
  );
};
